import _ from 'lodash'
import type { CompVariantsViewItem } from '@wix/thunderbolt-catharsis'
import type { CssValue, SelectorObj } from '../../shared.types'
import {
	getVariantSelectors,
	variantsComparator,
	getVariantIds,
	VARIANTS_SEPARATOR,
	isRepeaterPattern,
} from '../../utils/variantsUtils'
import { fromClassicCss } from '../../utils/fromClassicCss'
import type { DomApplier } from '../cssFeatures.types'
import { isBreakpointRange } from '../../utils/is'
import type { BaseDataItem, InnerVariant } from '@wix/thunderbolt-becky-types'
import { COMPONENTS_WITH_PORTAL_PREFIXES } from '@wix/thunderbolt-becky-types'
import type { SelectorToCssVars } from './styleBased.types'

export const FALLBACK_MINIMUM_VIEWPORT = 320
export const FALLBACK_MAXIMUM_VIEWPORT = 1920

const cleanPatternsVariants = (variantKey: string, themeVariants: CompVariantsViewItem | null) =>
	getVariantIds(variantKey)
		.filter(
			(variantId) => themeVariants?.[variantId] && !isRepeaterPattern(themeVariants?.[variantId] as InnerVariant)
		)
		.join(VARIANTS_SEPARATOR)

const getInnerElementsCss = (
	compId: string,
	breakpointId: string,
	innerElementTheme: any
): SelectorToCssVars | null => {
	if (!innerElementTheme?.innerElementsSelectors || _.isEmpty(innerElementTheme?.css)) {
		return null
	}

	return _.reduce(
		innerElementTheme.css,
		(acc, cssObj, selector) => {
			if (!cssObj[breakpointId]) {
				return acc
			}
			acc[innerElementTheme.innerElementsSelectors[compId + '/' + selector]] = cssObj[breakpointId]
			return acc
		},
		{} as SelectorToCssVars
	)
}

export const styleBasedDomApplier: DomApplier<'styleBased'> = (
	{ id: compId, componentType },
	idSelector,
	breakpointId,
	{ themeVariants, compTheme, viewportSize, innerElementTheme },
	accumulatedData
) => {
	const innerElementsCss = getInnerElementsCss(compId, breakpointId, innerElementTheme)
	let compThemeCss = {}

	if (compTheme) {
		if (compTheme.type === 'classic') {
			return fromClassicCss(compTheme.css as CssValue, idSelector)
		}

		const cssInBreakpoint = compTheme.css?.[breakpointId]
		if (cssInBreakpoint) {
			const shouldFixMinBreakpointTextScale = viewportSize?.shouldFixMinBreakpointTextScale || false
			const viewportSizeVars = getViewportSizeVars(
				idSelector,
				viewportSize?.[breakpointId],
				themeVariants?.[breakpointId],
				shouldFixMinBreakpointTextScale
			)

			compThemeCss = Object.entries(cssInBreakpoint)
				.sort(variantsComparator(themeVariants!))
				.reduce<SelectorObj>((acc, [variantKey, cssObj]) => {
					const domSelector = getVariantSelectors(variantKey, themeVariants, compId, accumulatedData).join(
						','
					)
					acc[domSelector] = Object.assign(acc[domSelector] || {}, cssObj)

					const prefix = COMPONENTS_WITH_PORTAL_PREFIXES[componentType]
					if (!prefix) {
						return acc
					}

					// TODO: remove this when we move to classes and assign the component class to the portal
					const portalDomSelector = getVariantSelectors(
						cleanPatternsVariants(variantKey, themeVariants),
						themeVariants,
						`${prefix}${compId}`,
						{}
					).join(',')
					acc[portalDomSelector] = Object.assign(acc[portalDomSelector] || {}, cssObj)

					return acc
				}, viewportSizeVars)
		}
	}

	return { ...compThemeCss, ...innerElementsCss }
}

const getViewportSizeVars = (
	idSelector: string,
	viewportSize: boolean | undefined,
	breakpointRange: BaseDataItem | undefined,
	shouldFixMinBreakpointTextScale: boolean
): SelectorObj => {
	if (!viewportSize || !isBreakpointRange(breakpointRange)) {
		return {}
	}

	const minViewportSize = shouldFixMinBreakpointTextScale ? (Math.max(breakpointRange.min || 0, FALLBACK_MINIMUM_VIEWPORT)) : (breakpointRange.min || FALLBACK_MINIMUM_VIEWPORT)

	const maxViewportSize = breakpointRange.max || FALLBACK_MAXIMUM_VIEWPORT

	return {
		[idSelector]: {
			'--minViewportSize': minViewportSize,
			'--maxViewportSize': maxViewportSize
		}
	}
}
